import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const InvoiceProfessionalTemplate = ({
    invoice = {},
    enquiry = {},
    quotation = {},
    invoiceData = [],
    paymentHistory = [],
    selectedValue,
    bankAc,
    headerBase64,
    footerBase64,
    footerAspectRatio,
    dgSignBase64,
    dgSignAspectRatio,
    numberToWords,
    orgDetails = {},
    type = "INVOICE",
}) => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 8;
    const pad = 4; // consistent inner padding from the outer border, used everywhere
    const contentX = margin + pad;
    const contentRight = pageWidth - margin - pad;
    const contentW = contentRight - contentX;

    const blue = [18, 54, 88];
    const lightBlue = [240, 246, 252];
    const border = [215, 215, 215];
    const gray = [90, 90, 90];
    const green = [22, 163, 74];
    const red = [220, 38, 38];
    const bottomLimit = pageHeight - 10;

    let y = 8;

    const n = (v, fallback = 0) => {
        const num = Number(v);
        return Number.isFinite(num) ? num : fallback;
    };

    const money = (v) =>
        n(v).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const formatDate = (v) =>
        v
            ? new Date(v).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            })
            : "-";

    const stripHtml = (html = "") =>
        String(html)
            .replace(/<li[^>]*>/gi, "\n- ")
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/<\/p>/gi, "\n")
            .replace(/<[^>]+>/g, "")
            .replace(/&nbsp;/g, " ")
            .trim();

    const addPageIfNeeded = (neededHeight = 20) => {
        if (y + neededHeight > bottomLimit) {
            doc.addPage();
            doc.setDrawColor(...border);
            doc.rect(margin, 6, pageWidth - margin * 2, 285);
            y = 12;
        }
    };

    const drawLabelValue = (label, value, x, yPos, labelW = 22) => {
        const xx = n(x);
        const yy = n(yPos);
        const lw = n(labelW, 22);
        doc.setFontSize(6.2);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...gray);
        doc.text(label, xx, yy);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(20, 20, 20);
        doc.text(":", xx + lw, yy);
        doc.text(String(value || "-"), xx + lw + 3, yy);
    };

    // ── OUTER BORDER ──────────────────────────────────────────────
    doc.setDrawColor(...border);
    doc.rect(margin, 6, pageWidth - margin * 2, 285);

    // ── HEADER ────────────────────────────────────────────────────
    const headerH = 33;
    const leftW = 70;
    const midW = 58;

    if (headerBase64) {
        try {
            doc.addImage(headerBase64, "PNG", contentX + 1, y + 1, 12, 12);
        } catch (e) {
            console.error("header image failed:", e);
        }
    }

    // Company name is split into two differently-colored chunks if it contains
    // a space (e.g. "Shreesh" + "Tech"); otherwise rendered as a single string.
    const fullCompanyName = orgDetails?.name || "";
    const splitIdx = Math.ceil(fullCompanyName.length / 2);
    const namePart1 = orgDetails?.name ? fullCompanyName : fullCompanyName.slice(0, splitIdx);
    const namePart2 = orgDetails?.name ? "" : fullCompanyName.slice(splitIdx);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(20, 20, 20);
    doc.text(namePart1, contentX + 20, y + 7);
    if (namePart2) {
        doc.setTextColor(11, 93, 148);
        doc.text(namePart2, contentX + 20 + doc.getTextWidth(namePart1), y + 7);
    }
    doc.setTextColor(20, 20, 20);
    doc.setFontSize(10);
    doc.text(orgDetails?.company_subtitle || "", contentX + 20, y + 11);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.text(orgDetails?.tagline || "", contentX, y + 17.5);
    doc.text(orgDetails?.tagline_2 || "", contentX, y + 21.5);

    const midX = contentX + leftW + 4;
    const contactLines = [
        orgDetails?.phone || "",
        orgDetails?.mail || "",
        orgDetails?.website || "",
        orgDetails?.address_1 || "",
        orgDetails?.address_2 || "",
    ];

    doc.setFontSize(6.5);
    doc.setTextColor(...gray);
    contactLines.forEach((line, i) => {
        doc.text(String(line || ""), midX, y + 6 + i * 4);
    });

    const rightX = contentX + leftW + midW + 4;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(11, 93, 148);
    doc.text("INVOICE", rightX, y + 7);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(11, 93, 148);
    doc.text(invoice.invoice_no || "-", rightX, y + 11.5);

    drawLabelValue("Invoice Date", formatDate(invoice.invoice_date), rightX, y + 16, 20);
    drawLabelValue("Due Date", formatDate(invoice.due_date), rightX, y + 19.5, 20);
    drawLabelValue("Reference", invoice.reference || "-", rightX, y + 23, 20);
    drawLabelValue("Project", invoice.project || "-", rightX, y + 26.5, 20);

    doc.setFontSize(6.2);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...gray);
    doc.text("Status", rightX, y + 30);
    doc.text(":", rightX + 20, y + 30);

    const statusColorMap = {
        Paid: green,
        "Paid Partially": [245, 158, 11],
        Sent: [37, 99, 235],
        Overdue: red,
        Draft: [180, 83, 9],
        Created: [37, 99, 235],
    };
    const statusText = invoice.status || "Draft";
    const statusColor = statusColorMap[statusText] || gray;
    doc.setTextColor(...statusColor);
    doc.setFont("helvetica", "bold");
    doc.text(statusText, rightX + 23, y + 30);

    doc.setDrawColor(220, 220, 220);
    doc.line(contentX + leftW, y + 1, contentX + leftW, y + 24);
    doc.line(contentX + leftW + midW, y + 1, contentX + leftW + midW, y + 24);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(contentX, y + headerH, contentRight, y + headerH);
    doc.setLineWidth(0.2);

    y += headerH;

    // ── BILL TO + PAYMENT SUMMARY (bordered boxes) ─────────────────
    const cardGap = 6;
    const halfW = (contentW - cardGap) / 2;
    const leftCardX = contentX;
    const rightCardX = contentX + halfW + cardGap;
    const cardTopY = y + 3;
    const cardBoxH = 38;

    doc.setDrawColor(...border);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(leftCardX, cardTopY, halfW, cardBoxH, 1.5, 1.5, "FD");
    doc.roundedRect(rightCardX, cardTopY, halfW, cardBoxH, 1.5, 1.5, "FD");

    doc.setFillColor(...lightBlue);
    doc.rect(leftCardX + 0.3, cardTopY + 0.3, halfW - 0.6, 7, "F");
    doc.rect(rightCardX + 0.3, cardTopY + 0.3, halfW - 0.6, 7, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...blue);
    doc.text("BILL TO", leftCardX + 5, cardTopY + 5.2);

    let billY = cardTopY + 12;
    const compName =
        enquiry?.company_name || enquiry?.name || quotation?.customer_company || "-";
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(30, 30, 30);
    doc.text(compName, leftCardX + 5, billY);
    billY += 4.5;

    doc.setFontSize(6.5);
    doc.setTextColor(30, 30, 30);

    const billRows = [
        ["Contact Person", enquiry?.contact_person || enquiry?.name || "-"],
        ["Mobile No", enquiry?.mobile_no || enquiry?.contact_whatsapp || "-"],
        ["Email", enquiry?.email || enquiry?.contact_email || "-"],
        ["GSTIN", enquiry?.gstin || "-"],
        ["Address", enquiry?.address || enquiry?.location_city || "-"],
    ];

    billRows.forEach(([label, value]) => {
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...gray);
        doc.text(label, leftCardX + 5, billY);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(30, 30, 30);
        doc.text(":", leftCardX + 27, billY);
        const wrapped = doc.splitTextToSize(String(value || "-"), Math.max(halfW - 38, 10));
        wrapped.forEach((line, i) => {
            doc.text(line, leftCardX + 30, billY + i * 3.2);
        });
        billY += Math.max(4, wrapped.length * 3.2);
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...blue);
    doc.text("PAYMENT SUMMARY", rightCardX + 5, cardTopY + 5.2);

    const invoiceValue = n(invoice.grand_total);
    const totalReceived = paymentHistory.reduce((sum, p) => sum + n(p.paid_amt), 0);
    const pendingAmount = Math.max(0, invoiceValue - totalReceived);
    const overdueAmount = invoice.status === "Overdue" ? pendingAmount : 0;

    const dueDatePassed = invoice.due_date && new Date(invoice.due_date) < new Date();
    const effectiveOverdue = dueDatePassed && pendingAmount > 0 ? pendingAmount : overdueAmount;

    const paymentProgress =
        invoiceValue > 0 ? Math.min(100, Math.round((totalReceived / invoiceValue) * 100)) : 0;

    let psY = cardTopY + 12;

    const psRows = [
        { label: "Invoice Value", value: money(invoiceValue), color: [30, 30, 30] },
        { label: "Received Amount", value: money(totalReceived), color: green },
        { label: "Pending Amount", value: money(pendingAmount), color: red },
        { label: "Overdue Amount", value: money(effectiveOverdue), color: effectiveOverdue > 0 ? red : [30, 30, 30] },
    ];

    psRows.forEach(({ label, value, color }) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(80, 80, 80);
        doc.text(label, rightCardX + 5, psY);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...color);
        doc.text(`: ${value}`, rightCardX + halfW - 5, psY, { align: "right" });
        psY += 4;
    });
    psY += 1.5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.2);
    doc.setTextColor(80, 80, 80);
    doc.text("Payment Progress", rightCardX + 5, psY);
    psY += 3;

    const barX = rightCardX + 5;
    const barW = Math.max(n(halfW) - 28, 5);
    const barH = 1.3;
    const safeProgress = n(paymentProgress);

    doc.setFillColor(230, 230, 230);
    doc.roundedRect(barX, psY, barW, barH, 1, 1, "F");

    if (safeProgress > 0) {
        doc.setFillColor(...green);
        doc.roundedRect(barX, psY, (barW * safeProgress) / 100, barH, 1, 1, "F");
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(30, 30, 30);
    doc.text(`${safeProgress}%`, barX + barW + 3, psY + 2);

    y = cardTopY + cardBoxH + 3;

    // ── ITEMS TABLE ────────────────────────────────────────────────
    addPageIfNeeded(50);

    const tableRows = invoiceData.map((item, idx) => {
        const qty = n(item.qty);
        const rate = n(item.unitprice);
        const discount = n(item.discount);
        const tax = n(item.tax);
        const base = qty * rate;
        const discountAmt = (base * discount) / 100;
        const taxable = base - discountAmt;
        const amount = item.totalprice != null ? n(item.totalprice) : Math.round(taxable + taxable * (tax / 100));

        return [
            idx + 1,
            item.description || item.service_name || "-",
            item.item_description || "-",
            qty,
            item.unit || "-",
            money(rate),
            `${discount}%`,
            `${tax}%`,
            money(amount),
        ];
    });

    autoTable(doc, {
        startY: y,
        margin: { left: contentX, right: margin + pad },
        tableWidth: contentW,
        head: [["#", "Item / Service", "Description", "Qty", "Unit", "Rate", "Discount (%)", "Tax (%)", "Amount"]],
        body: tableRows,
        theme: "grid",
        styles: {
            fontSize: 6.5,
            cellPadding: 1.8,
            valign: "middle",
            overflow: "linebreak",
            lineColor: border,
            lineWidth: 0.1,
        },
        headStyles: {
            fillColor: blue,
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "center",
            fontSize: 6.5,
        },
        columnStyles: {
            0: { cellWidth: 8, halign: "center" },
            1: { cellWidth: 32 },
            2: { cellWidth: 32 },
            3: { cellWidth: 10, halign: "center" },
            4: { cellWidth: 12, halign: "center" },
            5: { cellWidth: 22, halign: "right" },
            6: { cellWidth: 18, halign: "center" },
            7: { cellWidth: 14, halign: "center" },
            8: { cellWidth: "auto", halign: "right" },
        },
    });

    y = doc.lastAutoTable.finalY + 3;
    addPageIfNeeded(55);

    // ── AMOUNT IN WORDS + TOTALS ───────────────────────────────────
    let subTotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;

    invoiceData.forEach((item) => {
        const qty = n(item.qty);
        const rate = n(item.unitprice);
        const discount = n(item.discount);
        const tax = n(item.tax);
        const base = qty * rate;
        const discountAmt = (base * discount) / 100;
        const taxable = base - discountAmt;
        totalDiscount += discountAmt;
        subTotal += taxable;
        totalTax += (taxable * tax) / 100;
    });

    const cgst = totalTax / 2;
    const sgst = totalTax / 2;
    const grandTotal = Math.round(subTotal + totalTax);

    const summaryW = 80;
    const summaryX = contentRight - summaryW;
    const leftContentW = Math.max(summaryX - contentX - 8, 10);
    const wordsX = contentX;
    const wordsY = y;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...blue);
    doc.text("Amount in Words", wordsX, wordsY + 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.2);
    doc.setTextColor(30, 30, 30);
    const words = numberToWords?.(grandTotal) || String(grandTotal);
    doc.splitTextToSize(`Rupees ${words} Only`, leftContentW).slice(0, 2).forEach((line, i) => {
        doc.text(line, wordsX, wordsY + 8 + i * 3.2);
    });

    const firstTaxRate = invoiceData[0]?.tax ? n(invoiceData[0].tax) / 2 : 0;

    const summaryItems = [
        ["Sub Total", money(subTotal + totalDiscount)],
        ["Discount", money(totalDiscount)],
        ["Taxable Amount", money(subTotal)],
        [`CGST (${firstTaxRate}%)`, money(cgst)],
        [`SGST (${firstTaxRate}%)`, money(sgst)],
    ];

    let sy = wordsY;
    summaryItems.forEach(([label, value]) => {
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(...border);
        doc.rect(summaryX, sy, summaryW, 5, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.8);
        doc.setTextColor(60, 60, 60);
        doc.text(label, summaryX + 3, sy + 3.6);
        doc.setFont("helvetica", "normal");
        doc.text(`${value}`, summaryX + summaryW - 3, sy + 3.6, { align: "right" });
        sy += 5;
    });

    doc.setFillColor(...blue);
    doc.rect(summaryX, sy, summaryW, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text("Grand Total", summaryX + 3, sy + 4.8);
    doc.text(`${money(grandTotal)}`, summaryX + summaryW - 3, sy + 4.8, { align: "right" });
    sy += 8;

    y = Math.max(sy, wordsY + 16) + 1;
    addPageIfNeeded(45);

    // ── PAYMENT HISTORY TABLE (Total Received as last row) ──────────
    if (paymentHistory.length > 0) {
        try {
            doc.setFillColor(...lightBlue);
            doc.setDrawColor(...border);
            doc.rect(contentX, y, contentW, 6, "FD");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.5);
            doc.setTextColor(...blue);
            doc.text("Payment History", contentX + 3, y + 4.2);
            y += 6;

            const phRows = paymentHistory.map((p, i) => [
                i + 1,
                formatDate(p.payment_date),
                money(p.paid_amt),
                p.pay_mode || "-",
                p.reference_no || "-",
                p.notes || "-",
            ]);

            autoTable(doc, {
                startY: y,
                margin: { left: contentX, right: margin + pad },
                tableWidth: contentW,
                head: [["#", "Payment Date", "Amount", "Payment Mode", "Reference No", "Notes"]],
                body: phRows,
                theme: "grid",
                styles: { fontSize: 6.5, cellPadding: 1.6, lineColor: border, lineWidth: 0.1 },
                headStyles: { fillColor: [240, 246, 252], textColor: blue, fontStyle: "bold", fontSize: 6.5 },
                columnStyles: {
                    0: { cellWidth: 10, halign: "center" },
                    1: { cellWidth: 26 },
                    2: { cellWidth: 26, halign: "right" },
                    3: { cellWidth: 26 },
                    4: { cellWidth: 28 },
                    5: { cellWidth: "auto" },
                },
            });

            y = doc.lastAutoTable.finalY;

            doc.setFillColor(...lightBlue);
            doc.setDrawColor(...border);
            doc.rect(contentX, y, contentW, 6, "FD");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.setTextColor(30, 30, 30);
            doc.text("Total Received", contentX + 3, y + 4.2);
            doc.setTextColor(...green);
            doc.text(money(totalReceived), contentRight - 3, y + 4.2, { align: "right" });

            y += 6 + 3;
        } catch (e) {
            console.error("Payment history table failed to render:", e);
        }
    }

    addPageIfNeeded(40);

    // ── FOOTER 3-COLUMN: T&C | BANK DETAILS | THANK YOU / SIGNATURE ──
    const fColW = contentW / 3;
    const fStartX = contentX;
    const fY = y + 1;
const fH = 44;

    doc.setDrawColor(...border);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(fStartX, fY, fColW * 3, fH, 1.5, 1.5, "FD");

    doc.line(fStartX + fColW, fY, fStartX + fColW, fY + fH);
    doc.line(fStartX + fColW * 2, fY, fStartX + fColW * 2, fY + fH);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...blue);
    doc.text("TERMS & CONDITIONS", fStartX + 3, fY + 5.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.setTextColor(30, 30, 30);

    const rawTerms = invoice.tc_content || selectedValue?.data || "";
    const termsLines = rawTerms
        ? stripHtml(rawTerms).split("\n").filter(Boolean).slice(0, 6)
        : ["No terms and conditions selected."];

    let tcy = fY + 9;
    termsLines.forEach((term, i) => {
        const cleanTerm = term.replace(/^-\s*/, "").trim();
        const prefix = rawTerms ? `${i + 1}. ` : "";
        const lines = doc.splitTextToSize(`${prefix}${cleanTerm}`, fColW - 8).slice(0, 1);
        doc.text(lines[0], fStartX + 3, tcy);
        tcy += 3.6;
    });

    const bankX = fStartX + fColW + 3;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...blue);
    doc.text("BANK DETAILS", bankX, fY + 5.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.2);
    doc.setTextColor(30, 30, 30);

let bkY = fY + 9.5;
    if (bankAc) {
        const bankRows = [
            ["Bank Name", bankAc?.bank_name || "-"],
            ["Account Name", bankAc?.holder_name || bankAc?.account_name || "-"],
            ["Account Number", bankAc?.ac_no || bankAc?.account_no || "-"],
            ["IFSC Code", bankAc?.ifsc_code || "-"],
            ["Branch", bankAc?.branch || "-"],
        ];
        const bankLabelW = 24;
        const bankValueX = bankX + bankLabelW + 3;
        const bankValueMaxW = Math.max(fColW - bankLabelW - 3 - 6, 10);
        bankRows.forEach(([label, value]) => {
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...gray);
            doc.text(label, bankX, bkY);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(30, 30, 30);
            doc.text(":", bankX + bankLabelW, bkY);
            const wrapped = doc.splitTextToSize(String(value || "-"), bankValueMaxW);
            wrapped.forEach((line, i) => {
                doc.text(line, bankValueX, bkY + i * 3.2);
            });
            bkY += Math.max(4, wrapped.length * 3.2);
        });
    } else {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(30, 30, 30);
        doc.text("No bank account selected.", bankX, bkY);
    }

    const thkX = fStartX + fColW * 2 + 3;

    doc.setFont("helvetica", "bolditalic");
    doc.setFontSize(10.5);
    doc.setTextColor(...blue);
    doc.text("Thank You!", thkX + (fColW - 10) / 2, fY + 6, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.8);
    doc.setTextColor(80, 80, 80);
    doc.text("For your business.", thkX, fY + 11);
    doc.text(orgDetails?.signature_text || "", thkX, fY + 14.5);
    const sigColWidth = (fColW - 10) - 4;

    if (dgSignBase64) {
        try {
const maxW = Math.min(68, sigColWidth), maxH = 38;
            let signW = maxW, signH = maxH;
            if (dgSignAspectRatio) {
                if (maxW / maxH > dgSignAspectRatio) {
                    signH = maxH;
                    signW = maxH * dgSignAspectRatio;
                } else {
                    signW = maxW;
                    signH = maxW / dgSignAspectRatio;
                }
            }
            signW = Math.min(signW, maxW);
            signH = Math.min(signH, maxH);
            const signX = thkX + (fColW - 10 - signW) / 2;
doc.addImage(dgSignBase64, "PNG", signX, fY + 15, signW, signH);      } catch (e) {
            console.error("signature image failed:", e);
        }
    }

    doc.setDrawColor(80, 80, 80);
    doc.setLineWidth(0.25);
doc.line(thkX, fY + 39, thkX + fColW - 10, fY + 39);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.6);
    doc.setTextColor(80, 80, 80);
doc.text("Authorized Signatory", thkX, fY + 42.5);

    y = fY + fH + 4;
    if (footerBase64) {
        try {
            const footerImgW = pageWidth - margin * 2;
            const footerImgH = footerAspectRatio
                ? footerImgW / footerAspectRatio
                : 14;
            const footerImgY = pageHeight - footerImgH - 2;
            doc.addImage(
                footerBase64,
                "PNG",
                margin,
                footerImgY,
                footerImgW,
                footerImgH
            );
        } catch (e) {
            console.error("footer image failed:", e);
        }
    }

    return doc;
};

export default InvoiceProfessionalTemplate;